import { useEffect, useState } from "react";
import { useAuth } from "../../../../Context/AuthContext";
import { useServiceConfig } from "../../../../hooks/useServiceConfig";
import { useServiceApi } from "../../../../hooks/useServiceApi";
import { SERVICES } from "../../../../Context/serviceConstants";
import { resolveOrganizationId } from "../../../../Utils/organizationIdResolver";

// Which organisation's AGM & Board documents this account sees, and whether it manages them - shared by the
// AGM & Board cards page and the documents pages.
//
//   { loading, organizationId, isAdmin }
//
// 1. While the account's details are still loading, loading = true (pages wait instead of saying "not associated
//    with an organisation" - on the live server that message flashed, or stuck, for FPO admins and members).
// 2. The account's own FPO / company / membership (resolveOrganizationId) - admin when it holds this service's
//    admin role (fpoAdmin in My Org, businessAdmin in My Business).
// 3. Otherwise ask the server (GET /fpo/agm-documents/my-organisations): it applies the same rules as every AGM
//    call (own FPO or company, or a membership), so a member whose signed-in details lack the membership still
//    gets their organisation - read-only unless the server says they administer it.
export default function useAgmOrganisation() {
  const { fullUserData, normalizedRoles, accessToken } = useAuth();
  const { service } = useServiceConfig();
  const api = useServiceApi();
  const isBusinessContext = service === SERVICES.MY_BUSINESS;

  const localId = resolveOrganizationId(fullUserData);
  const roleAdmin = isBusinessContext ? normalizedRoles?.businessAdmin === true : normalizedRoles?.fpoAdmin === true;

  const [fallback, setFallback] = useState({ checked: false, organizationId: null, isAdmin: false });

  useEffect(() => {
    let cancelled = false;
    if (!fullUserData || localId || !accessToken) return undefined;
    api.getMyAgmOrganisations()
      .then((res) => {
        const orgs = res.data?.data || [];
        const want = isBusinessContext ? "MY_BUSINESS" : "MY_ORG";
        const org = orgs.find((o) => o.serviceKey === want) || orgs[0];
        if (!cancelled) setFallback({ checked: true, organizationId: org?.tenantId || null, isAdmin: org?.admin === true });
      })
      .catch(() => { if (!cancelled) setFallback({ checked: true, organizationId: null, isAdmin: false }); });
    return () => { cancelled = true; };
  }, [api, fullUserData, localId, accessToken, isBusinessContext]);

  if (!fullUserData) return { loading: !!accessToken, organizationId: null, isAdmin: false };
  if (localId) return { loading: false, organizationId: localId, isAdmin: roleAdmin };
  if (!fallback.checked) return { loading: true, organizationId: null, isAdmin: false };
  return { loading: false, organizationId: fallback.organizationId, isAdmin: fallback.isAdmin };
}
